import type { CosmeticRarity, CosmeticSlot, UserCosmeticLoadout } from '../../types';

export interface CosmeticCatalogItem {
  id: string;
  name: string;
  description: string;
  slot: CosmeticSlot;
  rarity: CosmeticRarity;
  costShells: number;
  unlockLevel?: number;
  unlockAchievementKey?: string;
  tournamentOnly?: boolean;
  seasonal?: boolean;
  starter?: boolean;
  emoji: string;
}

export const cosmeticCatalog: CosmeticCatalogItem[] = [
  {
    id: 'rookie_cap',
    name: 'Rookie Cap',
    description: 'A soft starter cap for new anglers finding their water legs.',
    slot: 'hat',
    rarity: 'common',
    costShells: 0,
    unlockLevel: 1,
    starter: true,
    emoji: '🧢',
  },
  {
    id: 'bay_hoodie',
    name: 'Baleen Bay Hoodie',
    description: 'Cozy community-core fit for cold dock mornings.',
    slot: 'top',
    rarity: 'common',
    costShells: 0,
    unlockLevel: 1,
    starter: true,
    emoji: '🧥',
  },
  {
    id: 'starter_rod_skin',
    name: 'Starter Rod Skin',
    description: 'Simple, trusty, and ready for first casts.',
    slot: 'rodSkin',
    rarity: 'common',
    costShells: 0,
    unlockLevel: 1,
    starter: true,
    emoji: '🎣',
  },
  {
    id: 'crew_ready_tee',
    name: 'Crew Ready Tee',
    description: 'A clean everyday layer for dock runs, crew meetups, and first light.',
    slot: 'top',
    rarity: 'uncommon',
    costShells: 95,
    unlockLevel: 2,
    emoji: '👕',
  },
  {
    id: 'lucky_minnow_charm',
    name: 'Lucky Minnow Charm',
    description: 'A tiny profile charm for bite-plan believers.',
    slot: 'lureCharm',
    rarity: 'uncommon',
    costShells: 75,
    unlockLevel: 2,
    emoji: '🐟',
  },
  {
    id: 'deckhand_beanie',
    name: 'Deckhand Beanie',
    description: 'Low-key dock warmth with a simple working-water look.',
    slot: 'hat',
    rarity: 'uncommon',
    costShells: 90,
    unlockLevel: 2,
    emoji: '🧶',
  },
  {
    id: 'harbor_bronze_frame',
    name: 'Harbor Bronze Frame',
    description: 'A warm profile frame for anglers with early progress.',
    slot: 'profileFrame',
    rarity: 'uncommon',
    costShells: 120,
    unlockLevel: 3,
    emoji: '⚓',
  },
  {
    id: 'dockside_cargos',
    name: 'Dockside Cargos',
    description: 'Practical field pants built around pockets, pliers, and a long day outside.',
    slot: 'pants',
    rarity: 'uncommon',
    costShells: 150,
    unlockLevel: 3,
    emoji: '👖',
  },
  {
    id: 'river_boots',
    name: 'River Boots',
    description: 'Simple trail-to-bank footwear for muddy launches and gravel bars.',
    slot: 'footwear',
    rarity: 'uncommon',
    costShells: 140,
    unlockLevel: 3,
    emoji: '🥾',
  },
  {
    id: 'harbor_windbreaker',
    name: 'Harbor Windbreaker',
    description: 'A light shell for breezy ramps, cool mornings, and moving water.',
    slot: 'outerwear',
    rarity: 'uncommon',
    costShells: 180,
    unlockLevel: 4,
    emoji: '🧥',
  },
  {
    id: 'cobalt_reel',
    name: 'Cobalt Reel',
    description: 'A clean blue reel skin inspired by deep water and polished hardware.',
    slot: 'reelSkin',
    rarity: 'uncommon',
    costShells: 180,
    unlockLevel: 4,
    emoji: '🌀',
  },
  {
    id: 'marsh_scout_title',
    name: 'Marsh Scout',
    description: 'For anglers who learn reeds, pads, and quiet water.',
    slot: 'title',
    rarity: 'rare',
    costShells: 250,
    unlockLevel: 5,
    emoji: '🌿',
  },
  {
    id: 'tidewatch_cap',
    name: 'Tidewatch Cap',
    description: 'A sharper cap for anglers who keep one eye on the water window.',
    slot: 'hat',
    rarity: 'rare',
    costShells: 220,
    unlockLevel: 5,
    emoji: '🧢',
  },
  {
    id: 'tidebreaker_top',
    name: 'Tidebreaker Top',
    description: 'A technical-looking layer with a clean Big Blue identity.',
    slot: 'top',
    rarity: 'rare',
    costShells: 260,
    unlockLevel: 5,
    emoji: '🌊',
  },
  {
    id: 'sunset_surf_rod',
    name: 'Sunset Surf Rod',
    description: 'A profile rod skin inspired by golden-hour surf checks.',
    slot: 'rodSkin',
    rarity: 'rare',
    costShells: 320,
    unlockLevel: 6,
    emoji: '🌅',
  },
  {
    id: 'sonar_glow_charm',
    name: 'Sonar Glow Charm',
    description: 'A small luminous charm for anglers who chase the next read.',
    slot: 'lureCharm',
    rarity: 'rare',
    costShells: 280,
    unlockLevel: 6,
    emoji: '✨',
  },
  {
    id: 'deepwater_boots',
    name: 'Deepwater Boots',
    description: 'A heavier field boot for cold banks, wet decks, and long sessions.',
    slot: 'footwear',
    rarity: 'rare',
    costShells: 300,
    unlockLevel: 6,
    emoji: '🥾',
  },
  {
    id: 'stormline_shell',
    name: 'Stormline Shell',
    description: 'Weather-ready outerwear with a restrained storm-water finish.',
    slot: 'outerwear',
    rarity: 'rare',
    costShells: 360,
    unlockLevel: 7,
    emoji: '🌧️',
  },
  {
    id: 'moonwake_reel',
    name: 'Moonwake Reel',
    description: 'A darker reel skin with moonlit-metal details for night anglers.',
    slot: 'reelSkin',
    rarity: 'rare',
    costShells: 340,
    unlockLevel: 7,
    emoji: '🌙',
  },
  {
    id: 'big_blue_background',
    name: 'Big Blue Background',
    description: 'A clean open-water backdrop for your Angler Card.',
    slot: 'background',
    rarity: 'rare',
    costShells: 420,
    unlockLevel: 8,
    emoji: '🌊',
  },
  {
    id: 'moonlight_frame',
    name: 'Moonlight Frame',
    description: 'A premium navy-and-moonlight profile frame with controlled Roddo plum.',
    slot: 'profileFrame',
    rarity: 'epic',
    costShells: 600,
    unlockLevel: 9,
    emoji: '🌌',
  },
  {
    id: 'night_current_rod',
    name: 'Night Current Rod',
    description: 'A dark-water rod skin for anglers who have put in real time on the app.',
    slot: 'rodSkin',
    rarity: 'epic',
    costShells: 650,
    unlockLevel: 10,
    emoji: '🎣',
  },
  {
    id: 'dockmaster_title',
    name: 'Dockmaster',
    description: 'A high-level title for anglers who have built some history around the Bay.',
    slot: 'title',
    rarity: 'epic',
    costShells: 700,
    unlockLevel: 12,
    emoji: '⚓',
  },
  {
    id: 'founding_tide_frame',
    name: 'Founding Tide Frame',
    description: "A permanent profile frame for Roddo's first 5,000 members.",
    slot: 'profileFrame',
    rarity: 'legendary',
    costShells: 0,
    seasonal: true,
    emoji: '🌊',
  },
  {
    id: 'founding_minnow_charm',
    name: 'Founding Minnow Charm',
    description: 'A launch-era charm reserved for Roddo founding members.',
    slot: 'lureCharm',
    rarity: 'legendary',
    costShells: 0,
    seasonal: true,
    emoji: '🐟',
  },
  {
    id: 'first_cast_founder_title',
    name: 'First Cast Founder',
    description: 'A permanent title for anglers who helped launch Roddo.',
    slot: 'title',
    rarity: 'legendary',
    costShells: 0,
    seasonal: true,
    emoji: '🚩',
  },
  {
    id: 'golden_bass_frame',
    name: 'Golden Bass Frame',
    description: 'Tournament prestige for your Angler Card.',
    slot: 'profileFrame',
    rarity: 'seasonal',
    costShells: 0,
    tournamentOnly: true,
    seasonal: true,
    emoji: '🏆',
  },
];

export const DEFAULT_COSMETIC_LOADOUT: UserCosmeticLoadout = {
  hat: 'rookie_cap',
  top: 'bay_hoodie',
  rodSkin: 'starter_rod_skin',
};

const SLOT_LABELS: Record<CosmeticSlot, string> = {
  hat: 'Hat',
  hair: 'Hair',
  faceCover: 'Face cover',
  top: 'Top',
  outerwear: 'Outerwear',
  pants: 'Pants',
  socks: 'Socks',
  footwear: 'Footwear',
  rodSkin: 'Rod skin',
  reelSkin: 'Reel skin',
  lureCharm: 'Lure charm',
  profileFrame: 'Profile frame',
  badgeA: 'Badge A',
  badgeB: 'Badge B',
  badgeC: 'Badge C',
  background: 'Background',
  title: 'Title',
};

export function getCosmeticById(id?: string | null): CosmeticCatalogItem | null {
  if (!id) return null;
  return cosmeticCatalog.find((item) => item.id === id) ?? null;
}

export function getCosmeticSlotLabel(slot: CosmeticSlot): string {
  return SLOT_LABELS[slot] || slot;
}

export function resolveCosmeticLoadout(loadout?: UserCosmeticLoadout | null): UserCosmeticLoadout {
  return {
    ...DEFAULT_COSMETIC_LOADOUT,
    ...(loadout || {}),
  };
}

export function getEquippedCosmetics(loadout?: UserCosmeticLoadout | null): CosmeticCatalogItem[] {
  const resolved = resolveCosmeticLoadout(loadout);
  return Object.values(resolved)
    .map((id) => getCosmeticById(id))
    .filter(Boolean) as CosmeticCatalogItem[];
}

export function getStarterCosmetics(): CosmeticCatalogItem[] {
  return cosmeticCatalog.filter((item) => item.starter);
}

export function getPreviewShopCosmetics(level: number, shells: number): CosmeticCatalogItem[] {
  return cosmeticCatalog
    .filter(
      (item) =>
        !item.starter &&
        !item.tournamentOnly &&
        !item.seasonal &&
        item.costShells > 0,
    )
    .map((item) => ({ ...item, unlockLevel: item.unlockLevel ?? 1 }))
    .sort((a, b) => {
      const aLocked = level < (a.unlockLevel ?? 1) ? 1 : 0;
      const bLocked = level < (b.unlockLevel ?? 1) ? 1 : 0;
      if (aLocked !== bLocked) return aLocked - bLocked;
      const aAffordable = shells >= a.costShells ? 0 : 1;
      const bAffordable = shells >= b.costShells ? 0 : 1;
      if (aAffordable !== bAffordable) return aAffordable - bAffordable;
      return a.costShells - b.costShells;
    });
}
